import React from 'react';

/**
 * Renders text with minimal inline Markdown: **bold** and *italic* only.
 * All other characters are escaped. No HTML, no links, safe for user content.
 */
type Segment = { type: 'text'; value: string } | { type: 'bold'; value: string } | { type: 'italic'; value: string };

function parseInlineMarkdown(input: string): Segment[] {
  if (typeof input !== 'string' || input.length === 0) return [{ type: 'text', value: '' }];
  const out: Segment[] = [];
  let rest = input;

  while (rest.length > 0) {
    const boldMatch = rest.match(/^\*\*(.+?)\*\*/s);
    const italicMatch = rest.match(/^\*([^*]+)\*/);

    if (boldMatch) {
      out.push({ type: 'bold', value: boldMatch[1] });
      rest = rest.slice(boldMatch[0].length);
      continue;
    }
    if (italicMatch) {
      out.push({ type: 'italic', value: italicMatch[1] });
      rest = rest.slice(italicMatch[0].length);
      continue;
    }

    const nextBold = rest.indexOf('**');
    const nextItalic = rest.indexOf('*');
    const next =
      nextBold === -1 && nextItalic === -1
        ? rest.length
        : nextBold === -1
          ? nextItalic
          : nextItalic === -1
            ? nextBold
            : Math.min(nextBold, nextItalic);

    if (next > 0) {
      out.push({ type: 'text', value: rest.slice(0, next) });
      rest = rest.slice(next);
    } else {
      out.push({ type: 'text', value: rest.slice(0, 1) });
      rest = rest.slice(1);
    }
  }

  return out.length ? out : [{ type: 'text', value: '' }];
}

interface MarkdownInlineProps {
  text: string;
  className?: string;
  /** If true, preserve newlines as <br /> (e.g. for message body) */
  preserveNewlines?: boolean;
  as?: 'span' | 'p';
}

function renderSegments(segments: Segment[]): React.ReactNode[] {
  return segments.map((seg, i) => {
    if (seg.type === 'text') return <React.Fragment key={i}>{seg.value}</React.Fragment>;
    if (seg.type === 'bold') return <strong key={i}>{renderSegments(parseInlineMarkdown(seg.value))}</strong>;
    return <em key={i}>{renderSegments(parseInlineMarkdown(seg.value))}</em>;
  });
}

export function MarkdownInline({ text, className, preserveNewlines, as: Component = 'span' }: MarkdownInlineProps) {
  const normalized = typeof text !== 'string' ? '' : text;
  const parts = preserveNewlines
    ? normalized.split(/\n/).map((line, i, arr) => (
        <React.Fragment key={i}>
          {renderSegments(parseInlineMarkdown(line))}
          {i < arr.length - 1 && <br />}
        </React.Fragment>
      ))
    : renderSegments(parseInlineMarkdown(normalized));

  return <Component className={className}>{parts}</Component>;
}
